import { useState } from 'react';

import { format } from 'date-fns';

import { AddProfileImage } from '@components/add-profile-image';
import {
  FormValidatorType,
  createForm,
  useCreateFormStore,
  useFormContext,
} from '@components/form';
import { FormError } from '@components/form-error';
import { IconEdit } from '@components/icons/edit';
import { IconTravellerCreate } from '@components/icons/traveller-create';
import { SelectOption } from '@components/selectmulti';
import { Sidebar, forceHideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { hasNumericValue } from '@utils/numbers';
import { getKeys } from '@utils/objects';

import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { TravellerAttributeTemplate } from 'api/attributes/requests';
import {
  useCompanyTraveller,
  useCreateCompanyTraveller,
  useEditCompanyTraveller,
} from 'api/company-traveller/hooks';
import { loadCompanyTravellers } from 'api/company-traveller/requests';
import { useList } from 'api/lists/hooks';
import { TravellerAttribute } from 'api/types';
import { useActiveUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';
import {
  companyTravellerAttributePrefix,
  getMultiOptionValuesArray,
} from 'ui/authenticated/main/helpers';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

export const useCompanyTravellerSidebar = () => {
  const sidebar = useSidebar(CreateCompanyTraveller, {
    variant: 'LARGE',
    closeOnClickOutside: true,
  });
  return sidebar;
};

type TravellerFormBaseFields = {
  name: string;
  email: string;
  phone: string;
};

type CompanyTravellerAttributes = {
  [K in `company_traveller_attribute__${string}`]: string | File | File[] | Date | SelectOption[];
};

type TravellerFormData = TravellerFormBaseFields & CompanyTravellerAttributes;

const Form = createForm<TravellerFormData>();

type CreateCompanyTravellerProps = {
  companyTravellerShortId?: string;
  workspaceId?: string;
  onBacktrack?: () => void;
};

const CreateCompanyTraveller = ({
  companyTravellerShortId,
  onBacktrack,
  workspaceId,
}: CreateCompanyTravellerProps) => {
  const companyTravellerAttributeTemplatesQuery = useCompanyTravellerAttributeTemplates();
  const companyTravellerAttributes = companyTravellerAttributeTemplatesQuery.data ?? [];
  const companyTravellerQuery = useCompanyTraveller(companyTravellerShortId ?? '');
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const createMutation = useCreateCompanyTraveller(forceHideSidebar);
  const editMutation = useEditCompanyTraveller(forceHideSidebar);

  const userQuery = useActiveUsers();

  const usersData = userQuery.data ?? [];

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);
  const [isPictureInvalid, setIsPictureInvalid] = useState(false);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const isDirty = formStore.useIsDirty() || !!picture || clearPicture;
  const companyTraveller = companyTravellerQuery.data;

  const isEdit = !!companyTraveller;

  const email = formStore.useFieldValue('email') as string;
  const formErrors: { [key: string]: string } = {};

  const checkEmail = async () => {
    if (!email) return;
    const response = await loadCompanyTravellers(`?email=${email}&page_size=1&page=1`);
    if (response.results.length > 0) {
      formStore.actions.updateErrors({ email: t('travellerExists') });
    }
  };

  const onFileMultiRemove = (file: string) => {
    setFilesToRemove([...filesToRemove, file]);
  };

  const handleSave = (data: TravellerFormData) => {
    if (isPictureInvalid) {
      return;
    }

    if (Object.keys(formErrors).length > 0) {
      return formStore.actions.setErrors(formErrors);
    }
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    if (workspaceId) formData.append('workspace', workspaceId);

    const keys = getKeys(data);

    if (companyTravellerAttributes) {
      if (!data) return;

      for (const key of keys) {
        if (key.startsWith(companyTravellerAttributePrefix)) {
          const value = data[key] ?? '';
          const attributeKey = key.replace(companyTravellerAttributePrefix, '');
          const attribute = companyTravellerAttributes.find(
            (attr) => attr.shortId === attributeKey
          );
          if (attribute?.attributeType === 'date' && value instanceof Date) {
            formData.append(key, value ? format(new Date(value), 'yyyy-MM-dd') : '');
          } else if (attribute?.attributeType === 'multi_select') {
            if (!Array.isArray(value)) {
              continue;
            }
            const arrayOfValues = getMultiOptionValuesArray(value);
            formData.append(key, JSON.stringify(arrayOfValues));
          } else if (attribute?.attributeType === 'file') {
            if (value instanceof File) {
              formData.append(key, value);
            } else if (!value) {
              formData.append(`${key}__remove`, '');
            }
          } else if (attribute?.attributeType === 'file_multi') {
            if (Array.isArray(value)) {
              value.forEach((file, index) => {
                if (file instanceof File) {
                  formData.append(`${key}__${index}`, file);
                }
              });
            }
            for (const fileShortId of filesToRemove) {
              formData.append(`${key}__remove__${fileShortId}`, fileShortId);
            }
          } else {
            formData.append(key, value as string);
          }
        }
      }
    }

    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    if (isEdit) {
      return editMutation.mutateAsync({ shortId: companyTraveller.shortId, data: formData });
    }
    return createMutation.mutateAsync(formData);
  };

  const onCancelSidebar = () => {
    forceHideSidebar();
    if (onBacktrack) onBacktrack();
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={isEdit ? t('editTraveller') : t('createTraveller')}
        icon={isEdit ? IconEdit : IconTravellerCreate}
        iconColor="elementBlue100"
      />
      <Sidebar.Content>
        {companyTravellerAttributeTemplatesQuery.isLoading ||
        (!companyTraveller && companyTravellerShortId) ? (
          <Sidebar.Spinner />
        ) : (
          <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
            <Stack space={4}>
              <Stack space={4} orientation="horizontal" align="center">
                <AddProfileImage
                  name={companyTraveller?.name ?? ''}
                  initalPicture={companyTraveller?.picture ?? ''}
                  picture={picture}
                  setPicture={setPicture}
                  clearPicture={clearPicture}
                  setClearPicture={setClearPicture}
                  setIsPictureInvalid={setIsPictureInvalid}
                />
                <div>
                  <Text font="semibold14" text={t('picture')} />
                  <Text font="regular14" text={t('acceptedFiles')} color="textHelper" />
                  {isPictureInvalid && <FormError text={t('invalidFileType')} />}
                </div>
              </Stack>
              <Form.TextField
                id="name"
                label={t('core.fullName')}
                validators={['required']}
                defaultValue={companyTraveller?.name ?? ''}
              />
              <Form.TextField
                id="email"
                label={t('core.email')}
                validators={['required', 'email']}
                onBlur={checkEmail}
                defaultValue={companyTraveller?.email ?? ''}
              />
              <Form.PhoneField
                id="phone"
                label={t('core.phone')}
                validators={['phone']}
                defaultValue={companyTraveller?.phone ?? ''}
              />
              {companyTravellerAttributes?.map((attribute) => {
                const traveller_attribute = companyTraveller?.companyTravellerAttributes.find(
                  (attr) => attr.attributeShortId === attribute.shortId
                );
                return (
                  attribute.isActive && (
                    <CompanyTravellerAttributeFields
                      key={attribute.shortId}
                      attributeTemplate={attribute}
                      usersData={usersData}
                      attribute={traveller_attribute}
                      onFileMultiRemove={onFileMultiRemove}
                    />
                  )
                );
              })}
            </Stack>
            <Sidebar.Footer>
              <Form.Actions>
                <Form.Reset text={t('core.cancel')} variant="secondary" onClick={onCancelSidebar} />
                <Form.Submit
                  text={isEdit ? t('core.save') : t('createTraveller')}
                  variant="primary-blue"
                  isDisabled={isEdit && !isDirty}
                />
              </Form.Actions>
            </Sidebar.Footer>
          </FormWithUnsavedChangesPrompt>
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

type CommonFieldProps = {
  id: keyof CompanyTravellerAttributes;
  label: string;
  validators?: FormValidatorType[];
  defaultValue?: string;
};

export const CompanyTravellerAttributeFields = ({
  attributeTemplate,
  attribute,
  usersData,
  onFileMultiRemove,
}: {
  attributeTemplate: TravellerAttributeTemplate;
  attribute?: TravellerAttribute;
  usersData?: User[];
  onFileMultiRemove?: (file: string) => void;
}) => {
  const { store } = useFormContext();
  const usersList = usersData?.map((user) => ({ value: user.shortId, label: user.name })) ?? [];

  const listQuery = useList(attributeTemplate.options || '');

  const fieldId: keyof CompanyTravellerAttributes = `${companyTravellerAttributePrefix}${attributeTemplate.shortId}`;

  const commonFieldProps: CommonFieldProps = {
    id: fieldId,
    label: attributeTemplate.templateTitle,
    defaultValue: attribute?.value
      ? attributeTemplate.attributeType === 'user'
        ? attribute.value?.shortId
        : attributeTemplate.attributeType === 'single_select'
        ? attribute.value.value
        : attribute.value
      : '',
    validators: attributeTemplate.isRequired ? ['required'] : [],
  };
  switch (attributeTemplate.attributeType) {
    case 'text':
      return <Form.TextField {...commonFieldProps} />;
    case 'paragraph':
      return <Form.Textarea {...commonFieldProps} />;
    case 'date':
      return <Form.DatePicker {...commonFieldProps} placeholder="DD / MM / YYYY" />;
    case 'user':
      return <Form.Select {...commonFieldProps} options={usersList} />;
    case 'phone_number':
      return <Form.PhoneField {...commonFieldProps} />;
    case 'email':
      return (
        <Form.TextField
          {...commonFieldProps}
          validators={[...(commonFieldProps.validators ?? []), 'email']}
        />
      );
    case 'number':
    case 'decimal':
      return (
        <Form.NumberField
          {...commonFieldProps}
          defaultValue={hasNumericValue(attribute?.value) ? attribute?.value : undefined}
          isDecimal={attributeTemplate.attributeType === 'decimal'}
        />
      );
    case 'single_select':
      return (
        <Form.Select
          {...commonFieldProps}
          options={(listQuery.data?.options ?? []).map((option) => ({
            value: option.shortId,
            label: option.label,
          }))}
        />
      );
    case 'multi_select':
      const optionValues = store.useFieldValue(fieldId) as SelectOption[];
      const chosenOptionsLabels = (optionValues ?? []).map((opt) => opt.value);
      const options = (listQuery.data?.options ?? []).map((option) => ({
        value: option.shortId,
        label: option.label,
      }));
      const filteredOptions = options.filter(
        (option) => !chosenOptionsLabels.includes(option.value)
      );
      return (
        <Form.SelectMulti
          defaultValue={attribute?.multiOptionValues}
          label={commonFieldProps.label}
          validators={commonFieldProps.validators}
          options={filteredOptions}
          // @ts-ignore
          id={fieldId}
        />
      );
    case 'file':
      const file = attribute?.files.length
        ? {
            id: attribute.files[0].id,
            name: attribute.files[0].name,
            src: attribute.files[0].src,
          }
        : undefined;
      return (
        <Form.FileField
          defaultValue={file}
          label={commonFieldProps.label}
          validators={commonFieldProps.validators}
          // @ts-ignore
          id={fieldId}
        />
      );
    case 'file_multi':
      const defultFiles = attribute?.files.length
        ? attribute?.files.map((file) => ({
            id: file.id,
            name: file.name,
            src: file.src,
          }))
        : [];
      return (
        <Form.FileFieldMulti
          // @ts-ignore
          id={fieldId}
          label={commonFieldProps.label}
          defaultValue={defultFiles}
          validators={commonFieldProps.validators}
          onRemove={(file) => {
            onFileMultiRemove?.(file.id);
          }}
        />
      );
    default:
      return null;
  }
};

const locale = {
  en: {
    createTraveller: 'Create traveller',
    editTraveller: 'Edit traveller',
    travellerAttributes: 'Traveller attributes',
    travellerExists: 'Traveller with this email already exists',
    picture: 'Profile image',
    acceptedFiles: 'Accepted file types: .png and .jpg',
    invalidFileType: 'This file type is not supported',
  },
};
